import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, withModifiers as _withModifiers, isRef as _isRef, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "content-grid content-grid--subpage content-grid--subpage-table" }
const _hoisted_2 = { class: "content-grid__header" }
const _hoisted_3 = ["onSubmit"]
const _hoisted_4 = { class: "form__submit" }
const _hoisted_5 = ["disabled"]

import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import Card from '@/components/layout/Card.vue';
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import BackLinkTitle from "@/components/layout/BackLinkTitle.vue";
import FormRow from "@/components/forms/FormRow.vue";
import SearchInput from "@/components/layout/SearchInput.vue";
import ActivityParticipantTable from "@/components/tables/ActivityParticipantTable.vue";

import {FormOption, Guid, TinyActivity} from "@/types";
import {useActivityOccurrenceService, useActivityService} from "@/inversify.config";
import {ActivityLocation, Participant} from "@/types/entities/activity";
import {BaseActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import {ComputedRef} from "vue/dist/vue";
import FormSearchableSelect from "@/components/forms/FormSearchableSelect.vue";

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'AdminActivityOccurrenceParticipants',
  props: {
    activityId: {},
    activityOccurrenceId: {}
  },
  async setup(__props: any) {

let __temp: any, __restore: any

const props = __props

const {t} = useI18n()

const activityService = useActivityService()
const activityOccurrenceService = useActivityOccurrenceService()

const selectedActivityId = ref(props.activityId ?? "")
const activities = ref<TinyActivity[]>((
  ([__temp,__restore] = _withAsyncContext(() => activityService.getAllActivities())),
  __temp = await __temp,
  __restore(),
  __temp
) ?? [])
const selectedActivity = ref<TinyActivity>(findActivityWithId(selectedActivityId.value))

const givenActivityOccurrenceId = ref(props.activityOccurrenceId ?? "")
const activityOccurrences = ref<BaseActivityOccurrence[]>(selectedActivity.value ? selectedActivity.value.activityOccurrences : [])
const selectedActivityOccurrence = ref<BaseActivityOccurrence>(activityOccurrences.value.length > 0 ? activityOccurrences.value[0] as BaseActivityOccurrence : new BaseActivityOccurrence())
const selectedActivityOccurrenceId = ref(Guid.isValid(givenActivityOccurrenceId.value) ? props.activityOccurrenceId : selectedActivityOccurrence.value.id ?? "")

const participants = ref<Participant[]>([]);
const participantsAreLoading = ref(false);
const participantsSearchValue = ref("");

onMounted(async () => {
  await fetchActivityOccurrenceParticipants()
})

const disableDownloadButton = computed(() => {
  return !selectedActivityId.value || !selectedActivityOccurrenceId.value || !participants.value.length
})

let activitiesOptions: ComputedRef<FormOption[]> = computed(() => {
  let formOptions = activities.value
      .filter(x => x && x.id && x.nameFr)
      .map(({id, nameFr}) => ({
        name: id,
        label: nameFr
      })) as FormOption[]
  return formOptions.sortByLabel()
});

let activityOccurrencesOptions: any = computed(() => {
  if (activityOccurrences.value.length === 0)
    return []
  return activityOccurrences.value
      .filter(x => x && x.id && x.startDate)
      .map(x => {
        let startDate = x.startDate ? x.startDate : ""
        let endDate = x.endDate ? ` ---> ${x.endDate}` : ''
        return {
          name: x.id,
          label: `${startDate}${endDate}`
        }
      });
});

let isOnlineActivity: any = computed(() => {
  if (!Guid.isValid(selectedActivityId.value))
    return false

  let activity = findActivityWithId(selectedActivityId.value)
  return activity?.location == ActivityLocation.Online
});

watch(selectedActivityId, (newId) => {
  participants.value = []
  selectedActivity.value = findActivityWithId(newId)

  activityOccurrences.value = selectedActivity.value.activityOccurrences
  if (activityOccurrences.value.length === 0)
    return
  selectedActivityOccurrence.value = activityOccurrences.value[0] as BaseActivityOccurrence
  selectedActivityOccurrenceId.value = activityOccurrences.value[0].id as string
})

watch(selectedActivityOccurrenceId, async () => {
  await fetchActivityOccurrenceParticipants()
})

function findActivityWithId(id: string): TinyActivity {
  return activities.value.find(x => x.id == id) as TinyActivity;
}

async function fetchActivityOccurrenceParticipants() {
  if (!selectedActivityOccurrenceId.value)
    return

  participantsAreLoading.value = true
  participants.value = await activityOccurrenceService.getActivityOccurrenceParticipants(selectedActivityOccurrenceId.value);
  participantsAreLoading.value = false
}

async function newParticipantAdded() {
  await fetchActivityOccurrenceParticipants()
}

async function handleSubmit() {
  let csvData = ";Num membre;Organisation;Prenom;Nom;Email";
  if (!isOnlineActivity.value)
    csvData += ";Allergies;Mobilite reduite"

  for (let index = 0; index < participants.value.length; index++) {
    const p = participants.value[index];
    const orgName = p.organizationName?.length ? p.organizationName : t("global.undefined");
    csvData += `\n${index + 1};${p.memberNumber?.length ?
      p.memberNumber :
      t("global.undefined")};${orgName};${p.firstName};${p.lastName};${p.emailAddress}`;
    if (!isOnlineActivity.value)
      csvData += `;${p.allergies?.length ?
          p.allergies :
          t("global.undefined")};${p.reducedMobility ? t("global.yes") : t("global.no")}`
  }

  const utf8BOM = "\uFEFF"; // Optional but recommended for proper UTF-8 encoding

  let blob = new Blob([utf8BOM, csvData], {type: "text/csv;charset=utf-8;"});
  let url = URL.createObjectURL(blob);
  let link = document.createElement("a");
  link.setAttribute("href", url);

  let activity = activities.value.find(x => x.id == selectedActivityId.value)
  link.setAttribute("download", `${activity?.nameFr}_participants.csv`);

  // Simulate a click on the link to initiate the download
  link.click();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Breadcrumbs, {
      title: _unref(t)(`routes.admin.children.activities.participants.add.name`)
    }, null, 8, ["title"]),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(BackLinkTitle, {
        title: _unref(t)(`routes.admin.children.activities.participants.add.name`)
      }, null, 8, ["title"]),
      (participants.value.length)
        ? (_openBlock(), _createBlock(SearchInput, {
            key: 0,
            modelValue: participantsSearchValue.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((participantsSearchValue).value = $event))
          }, null, 8, ["modelValue"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(Card, {
      title: _unref(t)('activities.selectAnActivity')
    }, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          class: "form",
          novalidate: "",
          onSubmit: _withModifiers(handleSubmit, ["prevent"])
        }, [
          _createVNode(FormRow, null, {
            default: _withCtx(() => [
              _createVNode(FormSearchableSelect, {
                modelValue: selectedActivityId.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedActivityId).value = $event)),
                label: _unref(t)('activities.activity'),
                options: _unref(activitiesOptions),
                rules: [],
                name: "activityId"
              }, null, 8, ["modelValue", "label", "options"]),
              (selectedActivityId.value)
                ? (_openBlock(), _createBlock(FormSearchableSelect, {
                    key: 0,
                    modelValue: selectedActivityOccurrenceId.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedActivityOccurrenceId).value = $event)),
                    label: _unref(t)('activities.selectAnOccurrence'),
                    options: _unref(activityOccurrencesOptions),
                    rules: [],
                    name: "activityOccurrenceId"
                  }, null, 8, ["modelValue", "label", "options"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("button", {
              disabled: disableDownloadButton.value,
              class: "btn btn--fullscreen",
              type: "submit"
            }, _toDisplayString(_unref(t)("participants.download")), 9, _hoisted_5)
          ])
        ], 40, _hoisted_3)
      ]),
      _: 1
    }, 8, ["title"]),
    (selectedActivity.value)
      ? (_openBlock(), _createBlock(ActivityParticipantTable, {
          key: 0,
          "activity-id": selectedActivityId.value,
          "onUpdate:activityId": _cache[3] || (_cache[3] = ($event: any) => ((selectedActivityId).value = $event)),
          "activity-occurrence-id": selectedActivityOccurrenceId.value,
          "onUpdate:activityOccurrenceId": _cache[4] || (_cache[4] = ($event: any) => ((selectedActivityOccurrenceId).value = $event)),
          isOnlineActivity: _unref(isOnlineActivity),
          "onUpdate:isOnlineActivity": _cache[5] || (_cache[5] = ($event: any) => (_isRef(isOnlineActivity) ? (isOnlineActivity).value = $event : isOnlineActivity = $event)),
          "max-participants": selectedActivity.value.maxParticipants,
          "onUpdate:maxParticipants": _cache[6] || (_cache[6] = ($event: any) => ((selectedActivity.value.maxParticipants) = $event)),
          participants: participants.value,
          "onUpdate:participants": _cache[7] || (_cache[7] = ($event: any) => ((participants).value = $event)),
          participantsAreLoading: participantsAreLoading.value,
          "onUpdate:participantsAreLoading": _cache[8] || (_cache[8] = ($event: any) => ((participantsAreLoading).value = $event)),
          participantsSearchValue: participantsSearchValue.value,
          "onUpdate:participantsSearchValue": _cache[9] || (_cache[9] = ($event: any) => ((participantsSearchValue).value = $event)),
          onNewParticipantAdded: newParticipantAdded
        }, null, 8, ["activity-id", "activity-occurrence-id", "isOnlineActivity", "max-participants", "participants", "participantsAreLoading", "participantsSearchValue"]))
      : _createCommentVNode("", true)
  ]))
}
}

})